import * as React from 'react';
import {
    mergeStyles,
    components,
    CreatableExtraProps,
    SelectBaseProps,
    ControlProps,
    // @ts-expect-error
} from 'react-select';

// @ts-expect-error
import Creatable from 'react-select/creatable';

import {colors} from '../colors/colors';
import {Divider} from '../layout/divider';
import {StackLegacy} from '../layout/stack/stack-legacy';

import {getCustomMenuList} from './custom-menu-list';
import type {FooterButtonProps} from './select-picker-generic';
import {selectPickerGenericStyles} from './select-picker-generic-styles';

export type Option = {
    value: string;
    label: string;
};
export type SelectSize = 'normal' | 'mini';
export type ActionMeta = {
    action:
        | 'select-option'
        | 'deselect-option'
        | 'remove-value'
        | 'pop-value'
        | 'set-value'
        | 'clear'
        | 'create-option';
};

type Props = SelectBaseProps &
    CreatableExtraProps & {
        value?: Option | null | undefined;
        /** Unfortunately for now because of https://github.com/JedWatson/react-select/issues/2953, we have to use `value` and `label` keys */
        options: Option[];
        /** Not allowing multiple selections in this component */
        isMulti?: false;
        onChange?: (newValue: any, actionMeta: ActionMeta) => void;
        /** Added for simplicity, components usually need to handle clearing differently from changes */
        onClear?: () => void;
        controlIcon?: React.ComponentType<any>;
        footerButton?: FooterButtonProps;
        size?: SelectSize;
        hideDivider?: boolean;
        menuZIndex?: number;
    };

export function SelectPickerGenericCreatable(props: Props) {
    const {onChange, onClear, footerButton, ...restProps} = props;

    // @ts-expect-error
    const handleChange = (newValue, actionMeta) => {
        if (Array.isArray(newValue)) {
            // There's a bug, where backspacing to clear sends an array.
            // https://github.com/JedWatson/react-select/pull/3216 should fix it
            if (newValue.length === 0) {
                if (onClear) {
                    onClear();
                }

                return;
            }
        }

        if (actionMeta.action === 'clear' || newValue === null || typeof newValue === 'undefined') {
            if (onClear) {
                onClear();
            }
        } else if (onChange) {
            onChange(newValue, actionMeta);
        }
    };

    const componentStyles = {
        // @ts-expect-error
        indicatorSeparator: (base) => ({
            ...base,
            display: props.hideDivider ? 'none' : 'unset',
        }),
        // @ts-expect-error
        control: (base) => ({
            ...base,
            height: props.size === 'mini' && '24px',
            minHeight: props.size === 'mini' && '24px',
        }),
        // @ts-expect-error
        valueContainer: (base) => ({
            ...base,
            padding: props.size === 'mini' ? '0 6px' : '2px 8px',
            fontSize: props.size === 'mini' && '11px',
            position: props.size === 'mini' && 'static',
        }),
        // @ts-expect-error
        placeholder: (base) => ({
            ...base,
            fontSize: props.size === 'mini' && '11px',
        }),
        // @ts-expect-error
        input: (base) => ({
            ...base,
            padding: props.size === 'mini' && '0',
        }),
        // @ts-expect-error
        indicatorsContainer: (base) => ({
            ...base,
            alignSelf: props.size === 'mini' ? 'center' : 'stretch',
        }),
        // @ts-expect-error
        dropdownIndicator: (base) => ({
            ...base,
            padding: props.size === 'mini' ? '0 6px' : '8px',
            paddingTop: props.size === 'mini' ? '0' : '5px',
            paddingBottom: props.size === 'mini' ? '1px' : '5px',
        }),
        // @ts-expect-error
        menu: (base) => {
            let menuStyles = {
                ...base,
            };

            if (props.menuZIndex) {
                menuStyles = {
                    ...menuStyles,
                    zIndex: props.menuZIndex,
                };
            }

            return menuStyles;
        },
        ...(footerButton && {
            // @ts-expect-error
            menuList: (base) => ({
                ...base,
                paddingBottom: 42,
            }),
        }),
    } as const;

    const mergedStyles = mergeStyles(selectPickerGenericStyles, componentStyles);

    return (
        <Creatable
            {...restProps}
            isMulti={false}
            getOptionValue={undefined}
            getOptionLabel={undefined}
            onChange={handleChange}
            styles={mergedStyles}
            components={{
                Control,
                MenuList: getCustomMenuList(footerButton),
                ...(props.components ? props.components : {}),
            }}
        />
    );
}

const Control = ({children, ...props}: ControlProps) => (
    <components.Control {...props}>
        {props.selectProps.controlIcon ? (
            <StackLegacy spacing={8} isHorizontal={true}>
                <div
                    style={{
                        marginTop: 2,
                        marginLeft: 8,
                    }}
                >
                    <props.selectProps.controlIcon
                        color={colors.grey}
                        size={13}
                        wrapWithDiv={false}
                    />
                </div>
                <Divider variant='line--vertical' />
            </StackLegacy>
        ) : undefined}
        {children}
    </components.Control>
);
